import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import Upload from 'lucide-react/dist/esm/icons/upload';
import FileSpreadsheet from 'lucide-react/dist/esm/icons/file-spreadsheet';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Download from 'lucide-react/dist/esm/icons/download';
import Plus from 'lucide-react/dist/esm/icons/plus';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import X from 'lucide-react/dist/esm/icons/x';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Check from 'lucide-react/dist/esm/icons/check';
import Save from 'lucide-react/dist/esm/icons/save';
import { useAmlStore } from '../../stores/amlStore';
import { amlApi } from '../../services/amlApi';
import {
  Button, Modal, Text, Badge, Spinner, Select, Alert,
  IconButton, Tooltip, TextField, DatePicker, NumericStepper,
} from '../../../../components/ui';
import { useToast } from '../../../../components/feedback';
import type {
  AmlCustomer,
  XlsxParseResponse,
  XlsxCustomerOption,
  XlsxLocationOption,
  XlsxImportDraftReviewState,
} from '../../types/aml';
import { useTranslation } from '../../../../hooks';
import { useUIStore } from '../../../../stores/uiStore';

interface TransactionImportFormProps {
  onClose: () => void;
}

type Step = 'upload' | 'review' | 'result';

type ReviewFilter = 'all' | 'matched' | 'ai' | 'unresolved' | 'skipped';

interface ResolvedMapping {
  [compositeKey: string]: {
    customer_id: string;
    customer_name: string;
    location_id?: string;
    location_name?: string;
  };
}

function splitCompositeKey(key: string): { client: string; location: string | null } {
  if (key.includes('|||')) {
    const [client, location] = key.split('|||', 2);
    return { client, location };
  }
  return { client: key, location: null };
}

// ── Inline Create Customer Mini-Modal ──

interface CreateCustomerModalProps {
  clientName: string;
  /** Location name from import file (spreadsheet Location column) — used as AML location name. */
  fileLocationName: string | null;
  /** Customers already in AML (from parse). Used to detect existing legal name → add location only. */
  existingCustomers: XlsxCustomerOption[];
  onCreated: (customer: AmlCustomer) => void;
  onCancel: () => void;
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  clientName,
  fileLocationName,
  existingCustomers,
  onCreated,
  onCancel,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);
  const [name, setName] = useState(clientName);
  const [code, setCode] = useState<string>('');
  const [initialDate, setInitialDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const trimmedFileLocation = fileLocationName?.trim() ?? '';

  const existingMatch = useMemo(
    () => existingCustomers.find((c) => c.name === name.trim()) ?? null,
    [existingCustomers, name],
  );

  const handleSave = useCallback(async () => {
    const trimmed = name.trim();
    const errs: Record<string, string> = {};
    if (!trimmed) errs.name = tr('amlColName') + ' is required';

    const locFromFile = fileLocationName?.trim() ?? '';
    const amlLocationName = locFromFile || trimmed;

    if (!existingMatch) {
      if (!code || isNaN(Number(code))) errs.code = tr('amlColCode') + ' is required';
      if (!initialDate) errs.initialDate = tr('amlColInitialDate') + ' is required';
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const toastDetail = (err: any) =>
      err?.response?.data?.detail
        ? (Array.isArray(err.response.data.detail)
            ? err.response.data.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ')
            : err.response.data.detail)
        : tr('amlImportErrorCreateCustomerFailed');

    setSaving(true);
    try {
      if (existingMatch) {
        await amlApi.createLocation({
          aml_customer_id: existingMatch.id,
          name: amlLocationName,
        });
        const full = await amlApi.getCustomer(existingMatch.id);
        toastSuccess(tr('amlImportToastLocationAdded', { name: full.name }));
        onCreated(full);
        return;
      }

      try {
        const created = await amlApi.createCustomer({
          name: trimmed,
          code: Number(code),
          status: true,
          initial_date: initialDate,
          locations: [{ name: amlLocationName }] as any,
        });
        toastSuccess(tr('amlImportToastCustomerCreated', { name: created.name }));
        onCreated(created);
      } catch (createErr: any) {
        if (createErr?.response?.status === 409) {
          const d = createErr?.response?.data?.detail;
          const dup =
            typeof d === 'string' && d.toLowerCase().includes('legal name');
          if (dup) {
            const list = await amlApi.listCustomers({ search: trimmed, page_size: 100 });
            const hit = list.items?.find((c) => c.name === trimmed);
            if (hit) {
              await amlApi.createLocation({ aml_customer_id: hit.id, name: amlLocationName });
              const full = await amlApi.getCustomer(hit.id);
              toastSuccess(tr('amlImportToastLocationAdded', { name: full.name }));
              onCreated(full);
              return;
            }
          }
        }
        toastError(toastDetail(createErr));
      }
    } catch (err: any) {
      toastError(toastDetail(err));
    } finally {
      setSaving(false);
    }
  }, [
    name,
    code,
    initialDate,
    fileLocationName,
    existingMatch,
    tr,
    toastSuccess,
    toastError,
    onCreated,
  ]);

  return (
    <Modal isOpen={true} onClose={onCancel} size="sm">
      <Modal.Header
        title={existingMatch ? tr('amlImportModalTitleAddLocation') : tr('amlCreateCustomerModalTitle')}
        icon={existingMatch ? <MapPin size={16} /> : <Plus size={16} />}
        onClose={onCancel}
      />
      <Modal.Body className="space-y-3">
        <TextField
          label={tr('amlFilterCustomerLegalName')}
          value={name}
          onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
          placeholder={tr('amlFilterCustomerLegalName')}
          error={errors.name}
          required
        />
        {existingMatch ? (
          <div className="rounded-xl border border-status-info/25 bg-status-info/5 px-3 py-2.5">
            <Text variant="caption" className="text-content-secondary">{tr('amlImportCreateCustomerExistingHint')}</Text>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <NumericStepper
              label={tr('amlColCode')}
              value={code}
              onChange={(v) => { setCode(v); setErrors(p => ({ ...p, code: undefined })); }}
              placeholder={tr('amlColCode')}
              error={errors.code}
              min={0}
              required
            />
            <div>
              <Text variant="label" className="mb-1.5">{tr('amlColInitialDate')} <span className="text-red-400">*</span></Text>
              <DatePicker
                value={initialDate}
                onChange={setInitialDate}
                size="md"
              />
              {errors.initialDate && <p className="text-xs text-red-400 mt-1">{errors.initialDate}</p>}
            </div>
          </div>
        )}
        {trimmedFileLocation && (
          <div className="rounded-lg bg-surface-secondary px-3 py-2">
            <Text variant="caption" className="text-content-muted">{tr('amlCreateCustomerLabelLocationFromFile')}</Text>
            <Text variant="body" className="text-sm">{trimmedFileLocation}</Text>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" size="lg" onClick={onCancel}>{tr('amlBtnCancel')}</Button>
        <Button variant="primary" size="lg" loading={saving} onClick={handleSave}>
          {existingMatch ? tr('amlImportBtnAddLocation') : tr('amlBtnCreateCustomer')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Main Import Form ──

function fillMissingLocations(
  mappings: ResolvedMapping,
  locations: XlsxLocationOption[],
): ResolvedMapping {
  const byCustomer = new Map<string, XlsxLocationOption[]>();
  for (const loc of locations) {
    const list = byCustomer.get(loc.customer_id);
    if (list) list.push(loc);
    else byCustomer.set(loc.customer_id, [loc]);
  }

  const next: ResolvedMapping = { ...mappings };
  for (const [key, mapping] of Object.entries(next)) {
    if (!mapping.customer_id) continue;
    const locs = byCustomer.get(mapping.customer_id) ?? [];
    if (mapping.location_id) {
      const stillThere = locs.some((l) => l.id === mapping.location_id);
      if (stillThere) continue;
    }
    if (locs.length === 1) {
      next[key] = {
        ...mapping,
        location_id: locs[0].id,
        location_name: locs[0].name,
      };
    } else if (mapping.location_id && locs.length !== 1) {
      next[key] = { ...mapping, location_id: undefined, location_name: undefined };
    }
  }
  return next;
}

const TransactionImportForm: React.FC<TransactionImportFormProps> = ({ onClose }) => {
  const fetchTransactions = useAmlStore((s) => s.fetchTransactions);
  const { success, warning, info, error: showError } = useToast();
  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [savedFileName, setSavedFileName] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [hasServerDraft, setHasServerDraft] = useState(false);
  const hasServerDraftRef = useRef(false);
  const [draftMeta, setDraftMeta] = useState<{ savedAt: string; fileName: string | null } | null>(null);

  const [parseData, setParseData] = useState<XlsxParseResponse | null>(null);
  const [resolvedMappings, setResolvedMappings] = useState<ResolvedMapping>({});
  const [skippedClients, setSkippedClients] = useState<Set<string>>(new Set());
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, { customer_id: string; customer_name: string; confidence: string; location_id?: string; location_name?: string }>>({});
  const [acceptedAi, setAcceptedAi] = useState<Set<string>>(new Set());
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');

  const [importResult, setImportResult] = useState<{ imported: number; duplicates: number; errors: string[]; skipped: number } | null>(null);

  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    amlApi.getImportDraftMeta()
      .then((meta) => {
        if (cancelled) return;
        if (meta.exists) {
          hasServerDraftRef.current = true;
          setHasServerDraft(true);
          setDraftMeta({
            savedAt: meta.saved_at ?? new Date().toISOString(),
            fileName: meta.file_name,
          });
        } else {
          hasServerDraftRef.current = false;
          setHasServerDraft(false);
          setDraftMeta(null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && (f.name.toLowerCase().endsWith('.xlsx') || f.name.toLowerCase().endsWith('.xls'))) {
      setFile(f);
      setParseErrors([]);
    } else if (f) {
      setFile(null);
      setParseErrors([tr('amlImportClickToSelectFile')]);
    } else {
      setFile(null);
      setParseErrors([]);
    }
  }, [tr]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await amlApi.downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError(tr('amlImportClickToSelectFile'));
    }
  }, [showError, tr]);

  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true);
    setParseErrors([]);
    try {
      const data = await amlApi.parseXlsxImport(file);
      setParseData(data);
      setSavedFileName(file.name);

      if (data.errors.length > 0 && data.rows.length === 0) {
        setParseErrors(data.errors);
        return;
      }

      const initial: ResolvedMapping = {};
      const aiSugg: Record<string, { customer_id: string; customer_name: string; confidence: string; location_id?: string; location_name?: string }> = {};
      const autoAccepted = new Set<string>();

      const locationsByCustomer: Record<string, { id: string; name: string }[]> = {};
      for (const loc of data.all_locations ?? []) {
        if (!locationsByCustomer[loc.customer_id]) locationsByCustomer[loc.customer_id] = [];
        locationsByCustomer[loc.customer_id].push(loc);
      }

      const resolveLocation = (customerId: string, locId?: string, locName?: string) => {
        if (locId && locName) return { location_id: locId, location_name: locName };
        const locs = locationsByCustomer[customerId] ?? [];
        if (locs.length === 1) return { location_id: locs[0].id, location_name: locs[0].name };
        return { location_id: locId, location_name: locName };
      };

      const ofacSkipped = new Set<string>();
      const customersById = new Map(data.all_customers.map((c) => [c.id, c]));

      for (const [key, match] of Object.entries(data.matches)) {
        const matchedCustomer = match.customer_id
          ? customersById.get(match.customer_id)
          : undefined;
        if (matchedCustomer?.screening_status === 'potential_match') {
          ofacSkipped.add(key);
          continue;
        }
        if (match.status === 'matched' && match.customer_id && match.customer_name) {
          const loc = resolveLocation(match.customer_id, match.location_id, match.location_name);
          initial[key] = {
            customer_id: match.customer_id,
            customer_name: match.customer_name,
            ...loc,
          };
        } else if (match.status === 'ai_matched' && match.customer_id && match.customer_name && match.confidence) {
          const loc = resolveLocation(match.customer_id, match.location_id, match.location_name);
          aiSugg[key] = {
            customer_id: match.customer_id,
            customer_name: match.customer_name,
            confidence: match.confidence,
            ...loc,
          };
          if (match.confidence === 'high') {
            initial[key] = {
              customer_id: match.customer_id,
              customer_name: match.customer_name,
              ...loc,
            };
            autoAccepted.add(key);
          }
        }
      }
      setResolvedMappings(initial);
      setAiSuggestions(aiSugg);
      setAcceptedAi(autoAccepted);
      setSkippedClients(ofacSkipped);
      setStep('review');

    } catch {
      setParseErrors([tr('amlImportSupportedFormat')]);
    } finally {
      setParsing(false);
    }
  }, [file, tr]);

  const handleResolve = useCallback((compositeKey: string, customerId: string, locationId?: string) => {
    if (!parseData) return;
    const cust = parseData.all_customers.find(c => c.id === customerId);
    if (!cust) return;
    if (cust.screening_status === 'potential_match') {
      setSkippedClients(prev => {
        const next = new Set(prev);
        next.add(compositeKey);
        return next;
      });
      setResolvedMappings(prev => {
        const next = { ...prev };
        delete next[compositeKey];
        return next;
      });
      return;
    }
    const custLocations = (parseData.all_locations ?? []).filter(l => l.customer_id === customerId);
    let loc = locationId
      ? custLocations.find(l => l.id === locationId)
      : undefined;
    if (!loc && custLocations.length === 1) {
      loc = custLocations[0];
    }
    setResolvedMappings(prev => ({
      ...prev,
      [compositeKey]: {
        customer_id: cust.id,
        customer_name: cust.name,
        location_id: loc?.id,
        location_name: loc?.name,
      },
    }));
    setSkippedClients(prev => {
      const next = new Set(prev);
      next.delete(compositeKey);
      return next;
    });
  }, [parseData]);

  const handleResolveLocation = useCallback((compositeKey: string, locationId: string) => {
    if (!parseData) return;
    const loc = (parseData.all_locations ?? []).find(l => l.id === locationId);
    if (loc) {
      setResolvedMappings(prev => {
        const existing = prev[compositeKey];
        if (!existing) return prev;
        return {
          ...prev,
          [compositeKey]: { ...existing, location_id: loc.id, location_name: loc.name },
        };
      });
    }
  }, [parseData]);

  const handleCustomerCreated = useCallback((compositeKey: string, customer: AmlCustomer) => {
    const option: XlsxCustomerOption = { id: customer.id, name: customer.name };
    const newLocs: XlsxLocationOption[] = (customer.locations ?? []).map((loc) => ({
      id: loc.id,
      name: loc.name,
      customer_id: customer.id,
    }));

    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
    const { location: fileLocation } = splitCompositeKey(compositeKey);
    const locs = customer.locations ?? [];
    let chosen = locs.length === 1 ? locs[0] : undefined;
    if (!chosen && fileLocation && locs.length > 0) {
      const nf = norm(fileLocation);
      chosen =
        locs.find((l) => norm(l.name) === nf) ??
        locs.find((l) => norm(l.name).includes(nf) || nf.includes(norm(l.name)));
    }

    setParseData((prev) => {
      if (!prev) return prev;
      const hadCustomer = prev.all_customers.some((c) => c.id === customer.id);
      const mergedCustomers = hadCustomer
        ? prev.all_customers
        : [...prev.all_customers, option].sort((a, b) => a.name.localeCompare(b.name));
      const knownLocIds = new Set((prev.all_locations ?? []).map((l) => l.id));
      const locsToAdd = newLocs.filter((l) => !knownLocIds.has(l.id));
      return {
        ...prev,
        all_customers: mergedCustomers,
        all_locations: [...(prev.all_locations ?? []), ...locsToAdd],
      };
    });
    setResolvedMappings((prev) => ({
      ...prev,
      [compositeKey]: {
        customer_id: customer.id,
        customer_name: customer.name,
        ...(chosen ? { location_id: chosen.id, location_name: chosen.name } : {}),
      },
    }));
    setSkippedClients((prev) => {
      const next = new Set(prev);
      next.delete(compositeKey);
      return next;
    });
    setCreatingFor(null);
  }, []);

  const handleAcceptAi = useCallback((compositeKey: string) => {
    const suggestion = aiSuggestions[compositeKey];
    if (!suggestion) return;
    const blocked = parseData?.all_customers.find(
      (c) => c.id === suggestion.customer_id && c.screening_status === 'potential_match',
    );
    if (blocked) {
      setSkippedClients(prev => new Set(prev).add(compositeKey));
      return;
    }
    setResolvedMappings(prev => ({
      ...prev,
      [compositeKey]: {
        customer_id: suggestion.customer_id,
        customer_name: suggestion.customer_name,
        location_id: suggestion.location_id,
        location_name: suggestion.location_name,
      },
    }));
    setAcceptedAi(prev => new Set(prev).add(compositeKey));
    setSkippedClients(prev => {
      const next = new Set(prev);
      next.delete(compositeKey);
      return next;
    });
  }, [aiSuggestions, parseData]);

  const handleRejectAi = useCallback((compositeKey: string) => {
    setResolvedMappings(prev => {
      const next = { ...prev };
      delete next[compositeKey];
      return next;
    });
    setAcceptedAi(prev => {
      const next = new Set(prev);
      next.delete(compositeKey);
      return next;
    });
    setAiSuggestions(prev => {
      const next = { ...prev };
      delete next[compositeKey];
      return next;
    });
  }, []);

  const handleSkip = useCallback((compositeKey: string) => {
    setSkippedClients(prev => {
      const next = new Set(prev);
      next.add(compositeKey);
      return next;
    });
    setResolvedMappings(prev => {
      const next = { ...prev };
      delete next[compositeKey];
      return next;
    });
  }, []);

  const handleUnskip = useCallback((compositeKey: string) => {
    if (!parseData) return;
    const match = parseData.matches[compositeKey];
    const matched = match?.customer_id
      ? parseData.all_customers.find((c) => c.id === match.customer_id)
      : undefined;
    if (matched?.screening_status === 'potential_match') return;
    setSkippedClients(prev => {
      const next = new Set(prev);
      next.delete(compositeKey);
      return next;
    });
  }, [parseData]);

  const activeClients = useMemo(
    () => parseData ? parseData.distinct_clients.filter(key => !skippedClients.has(key)) : [],
    [parseData, skippedClients],
  );

  const allResolved = useMemo(
    () => activeClients.length > 0 && activeClients.every(key => {
      const m = resolvedMappings[key];
      return m && m.customer_id && m.location_id;
    }),
    [activeClients, resolvedMappings],
  );

  const matchedCount = useMemo(
    () => parseData ? activeClients.filter(key => parseData.matches[key]?.status === 'matched').length : 0,
    [parseData, activeClients],
  );

  const aiMatchedCount = useMemo(
    () => parseData ? activeClients.filter(key => parseData.matches[key]?.status === 'ai_matched').length : 0,
    [parseData, activeClients],
  );

  const unresolvedCount = useMemo(
    () => activeClients.filter(key => {
      const m = resolvedMappings[key];
      return !m || !m.customer_id || !m.location_id;
    }).length,
    [activeClients, resolvedMappings],
  );

  const skippedCount = skippedClients.size;
  const ofacSkippedCount = useMemo(() => {
    if (!parseData) return 0;
    const blockedIds = new Set(
      parseData.all_customers
        .filter((c) => c.screening_status === 'potential_match')
        .map((c) => c.id),
    );
    const blockedNames = new Set(
      parseData.all_customers
        .filter((c) => c.screening_status === 'potential_match')
        .map((c) => c.name),
    );
    return Array.from(skippedClients).filter((key) => {
      const match = parseData.matches[key];
      if (match?.customer_id && blockedIds.has(match.customer_id)) return true;
      const { client } = splitCompositeKey(key);
      return blockedNames.has(match?.customer_name || client);
    }).length;
  }, [parseData, skippedClients]);

  const unscreenedMappedCount = useMemo(() => {
    if (!parseData) return 0;
    const unscreenedIds = new Set(
      parseData.all_customers
        .filter((c) => !c.screening_status)
        .map((c) => c.id),
    );
    return activeClients.filter((key) => {
      const customerId = resolvedMappings[key]?.customer_id
        ?? parseData.matches[key]?.customer_id;
      return !!customerId && unscreenedIds.has(customerId);
    }).length;
  }, [parseData, activeClients, resolvedMappings]);

  useEffect(() => {
    setReviewFilter('all');
  }, [parseData]);

  const filteredDistinctClients = useMemo(() => {
    if (!parseData) return [];
    const keys = parseData.distinct_clients;
    switch (reviewFilter) {
      case 'all':
        return keys;
      case 'matched':
        return keys.filter(
          (k) => !skippedClients.has(k) && parseData.matches[k]?.status === 'matched',
        );
      case 'ai':
        return keys.filter(
          (k) => !skippedClients.has(k) && parseData.matches[k]?.status === 'ai_matched',
        );
      case 'unresolved':
        return keys.filter((k) => {
          if (skippedClients.has(k)) return false;
          const m = resolvedMappings[k];
          return !m || !m.customer_id || !m.location_id;
        });
      case 'skipped':
        return keys.filter((k) => skippedClients.has(k));
      default:
        return keys;
    }
  }, [parseData, reviewFilter, skippedClients, resolvedMappings]);

  const makeKey = useCallback((client: string, location?: string | null) => {
    return location ? `${client}|||${location}` : client;
  }, []);

  const skippedRowCount = useMemo(
    () => parseData ? parseData.rows.filter(r => skippedClients.has(makeKey(r.client, r.location))).length : 0,
    [parseData, skippedClients, makeKey],
  );

  const handleCommit = useCallback(async () => {
    if (!parseData || !allResolved) return;
    setCommitting(true);
    try {
      const mappings = Object.entries(resolvedMappings).map(([compositeKey, m]) => ({
        client_name: compositeKey,
        customer_id: m.customer_id,
        location_id: m.location_id,
      }));

      const filteredRows = parseData.rows.filter(
        r => !skippedClients.has(makeKey(r.client, r.location)),
      );

      const res = await amlApi.commitXlsxImport({
        rows: filteredRows,
        mappings,
      });

      setImportResult({ ...res, skipped: skippedRowCount });
      setStep('result');
      try { await amlApi.deleteImportDraft(); } catch { /* already gone */ }
      hasServerDraftRef.current = false;
      setHasServerDraft(false);
      setDraftMeta(null);

      if (res.errors.length > 0) {
        warning(
          tr('amlImportToastImportPartial', { imported: res.imported, issues: res.errors.length }),
        );
      } else {
        const toastParts = [tr('amlImportToastImportComplete', { imported: res.imported })];
        if (res.duplicates > 0) {
          toastParts.push(tr('amlImportToastImportDuplicates', { count: res.duplicates }));
        }
        success(toastParts.join(' '));
      }

      fetchTransactions();
    } catch {
      showError(tr('amlImportErrorImportFailed'));
    } finally {
      setCommitting(false);
    }
  }, [parseData, allResolved, resolvedMappings, skippedClients, skippedRowCount, makeKey, success, warning, showError, tr, fetchTransactions]);

  const persistDraft = useCallback(async (opts?: { silent?: boolean; reviewOnly?: boolean }) => {
    if (!parseData) return false;
    setSavingDraft(true);
    try {
      const file_name = file?.name ?? savedFileName;
      const review_state: XlsxImportDraftReviewState = {
        resolved_mappings: resolvedMappings,
        skipped_clients: Array.from(skippedClients),
        ai_suggestions: aiSuggestions,
        accepted_ai: Array.from(acceptedAi),
      };
      const meta = opts?.reviewOnly && hasServerDraftRef.current
        ? await amlApi.patchImportDraft({ file_name, review_state })
        : await amlApi.putImportDraft({ file_name, parse_data: parseData, review_state });
      hasServerDraftRef.current = true;
      setHasServerDraft(true);
      setDraftMeta({
        savedAt: meta.saved_at ?? new Date().toISOString(),
        fileName: meta.file_name,
      });
      if (!opts?.silent) {
        success(tr('amlImportToastDraftSaved'));
      }
      return true;
    } catch {
      showError(tr('amlImportErrorDraftSaveFailed'));
      return false;
    } finally {
      setSavingDraft(false);
    }
  }, [
    parseData, file, savedFileName, resolvedMappings,
    skippedClients, aiSuggestions, acceptedAi, success, showError, tr,
  ]);

  const resetWizard = useCallback(() => {
    setFile(null);
    setSavedFileName(null);
    setStep('upload');
    setParseData(null);
    setResolvedMappings({});
    setSkippedClients(new Set());
    setAiSuggestions({});
    setAcceptedAi(new Set());
    setParseErrors([]);
    setImportResult(null);
    setCreatingFor(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(async () => {
    if (parsing || committing || resuming) return;
    if (step === 'review' && parseData) {
      await persistDraft({ silent: true, reviewOnly: hasServerDraftRef.current });
    }
    resetWizard();
    onClose();
  }, [parsing, committing, resuming, step, parseData, persistDraft, resetWizard, onClose]);

  const handleSaveForLater = useCallback(async () => {
    const ok = await persistDraft({ reviewOnly: hasServerDraftRef.current });
    if (!ok) return;
    resetWizard();
    onClose();
  }, [persistDraft, resetWizard, onClose]);

  const handleResumeDraft = useCallback(async () => {
    setResuming(true);
    try {
      const draft = await amlApi.getImportDraft();
      let nextParse = draft.parse_data;
      let nextMappings = draft.review_state.resolved_mappings;
      try {
        const [custRes, locs] = await Promise.all([
          amlApi.listCustomers({ page: 1, page_size: 500 }),
          amlApi.listLocations(),
        ]);
        nextParse = {
          ...draft.parse_data,
          all_customers: custRes.items
            .map((c) => ({
              id: c.id,
              name: c.name,
              screening_status: c.screening_status,
            }))
            .toSorted((a, b) => a.name.localeCompare(b.name)),
          all_locations: locs.map((l) => ({
            id: l.id,
            name: l.name,
            customer_id: l.aml_customer_id,
          })),
        };
        nextMappings = fillMissingLocations(draft.review_state.resolved_mappings, nextParse.all_locations);
      } catch {
        nextMappings = fillMissingLocations(
          draft.review_state.resolved_mappings,
          draft.parse_data.all_locations ?? [],
        );
      }
      setParseData(nextParse);
      setResolvedMappings(nextMappings);
      setSkippedClients(new Set(draft.review_state.skipped_clients));
      setAiSuggestions(draft.review_state.ai_suggestions);
      setAcceptedAi(new Set(draft.review_state.accepted_ai));
      setSavedFileName(draft.file_name);
      setParseErrors([]);
      hasServerDraftRef.current = true;
      setHasServerDraft(true);
      setStep('review');
    } catch {
      setDraftMeta(null);
      hasServerDraftRef.current = false;
      setHasServerDraft(false);
      showError(tr('amlImportErrorDraftSaveFailed'));
    } finally {
      setResuming(false);
    }
  }, [showError, tr]);

  const handleDiscardDraft = useCallback(async () => {
    try {
      await amlApi.deleteImportDraft();
    } catch { /* already gone */ }
    hasServerDraftRef.current = false;
    setHasServerDraft(false);
    setDraftMeta(null);
    info(tr('amlImportToastDraftDiscarded'));
  }, [info, tr]);

  useEffect(() => {
    if (step !== 'review' || !parseData || !hasServerDraft) return;
    const timer = window.setTimeout(() => {
      void persistDraft({ silent: true, reviewOnly: true });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [step, parseData, hasServerDraft, persistDraft]);

  const handleBack = useCallback(async () => {
    if (parseData) {
      await persistDraft({ silent: true, reviewOnly: hasServerDraftRef.current });
    }
    setStep('upload');
    setParseData(null);
    setResolvedMappings({});
    setSkippedClients(new Set());
    setAiSuggestions({});
    setAcceptedAi(new Set());
    setCreatingFor(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [parseData, persistDraft]);

  const customerOptions = useMemo(
    () => parseData
      ? parseData.all_customers.map((c) => ({
          value: c.id,
          label: c.name,
          disabled: c.screening_status === 'potential_match',
          hint: c.screening_status === 'potential_match'
            ? tr('amlScreeningResultPotentialMatch')
            : c.screening_status == null
              ? tr('amlScreeningResultNotScreened')
              : undefined,
        }))
      : [],
    [parseData, tr],
  );

  const locationOptionsForCustomer = useCallback((customerId: string) => {
    if (!parseData) return [];
    return (parseData.all_locations ?? [])
      .filter(l => l.customer_id === customerId)
      .map(l => ({ value: l.id, label: l.name }));
  }, [parseData]);

  return (
    <>
      <Modal isOpen={true} onClose={handleClose} size={step === 'review' ? 'xl' : 'md'} className={step === 'review' ? '!max-w-5xl' : undefined}>
        <Modal.Header
          title={
            step === 'upload' ? tr('amlImportModalTitleUpload') :
            step === 'review' ? tr('amlImportModalTitleReview') :
            tr('amlImportModalTitleResult')
          }
          icon={<Upload size={16} />}
          onClose={handleClose}
        />

        <Modal.Body className="space-y-4">
          {/* ── Step 1: Upload ── */}
          {step === 'upload' && !parsing && !resuming && (
            <>
              {draftMeta && (
                <Alert variant="info" title={tr('amlImportDraftBannerTitle')} className="!p-3">
                  <Text variant="caption" className="text-content-secondary">
                    {draftMeta.fileName
                      ? tr('amlImportDraftBannerBody', {
                          fileName: draftMeta.fileName,
                          date: new Date(draftMeta.savedAt).toLocaleString(),
                        })
                      : tr('amlImportDraftBannerBodyNoFile', {
                          date: new Date(draftMeta.savedAt).toLocaleString(),
                        })}
                  </Text>
                  <div className="flex items-center gap-2 mt-3">
                    <Button variant="primary" size="sm" onClick={handleResumeDraft}>
                      {tr('amlImportBtnResumeDraft')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleDiscardDraft}>
                      {tr('amlImportBtnDiscardDraft')}
                    </Button>
                  </div>
                </Alert>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  id="transaction-import-file"
                />
                <label
                  htmlFor="transaction-import-file"
                  className="flex flex-col items-center justify-center w-full h-32 rounded-xl border-2 border-dashed border-subtle hover:border-default cursor-pointer transition-colors bg-surface-primary/30"
                >
                  <FileSpreadsheet size={28} className="text-content-muted mb-2" />
                  <span className="text-sm text-content-secondary">
                    {file ? file.name : tr('amlImportClickToSelectFile')}
                  </span>
                  {!file && (
                    <span className="text-2xs text-content-muted mt-1">
                      {tr('amlImportSupportedFormat')}
                    </span>
                  )}
                </label>
              </div>

              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="flex items-center gap-1.5 text-xs text-content-secondary hover:text-content transition-colors mx-auto"
              >
                <Download size={12} />
                {tr('amlImportDownloadSampleTemplate')}
              </button>

              {parseErrors.length > 0 && (
                <div className="rounded-xl border border-status-error/20 bg-status-error/5 p-4 space-y-1">
                  {parseErrors.map((err, i) => (
                    <p key={i} className="text-xs text-content-secondary">{err}</p>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Parsing spinner */}
          {(parsing || resuming) && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Spinner size="lg" />
              <Text variant="body" className="text-content-secondary text-sm">
                {resuming ? tr('amlImportBtnResumeDraft') : tr('amlImportParsingFile')}
              </Text>
              {parsing && (
                <div className="flex items-center gap-1.5">
                  <Sparkles size={12} className="text-purple-400" />
                  <Text variant="caption" className="text-purple-400/70">
                    {tr('amlImportAiMatchingEnabled')}
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Review ── */}
          {step === 'review' && parseData && (
            <>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewFilter('all')}
                  className={`h-auto min-h-0 justify-start gap-2 rounded-lg bg-surface-secondary px-3 py-2 font-normal ${
                    reviewFilter === 'all' ? 'ring-2 ring-inset ring-content-muted/50' : ''
                  }`}
                >
                  <Text variant="caption" className="text-content-muted">{tr('amlImportStatTotalRows')}</Text>
                  <Text variant="label" className="!text-content">{parseData.total_rows}</Text>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewFilter('all')}
                  className={`h-auto min-h-0 justify-start gap-2 rounded-lg bg-surface-secondary px-3 py-2 font-normal ${
                    reviewFilter === 'all' ? 'ring-2 ring-inset ring-content-muted/50' : ''
                  }`}
                >
                  <Text variant="caption" className="text-content-muted">{tr('amlImportStatClients')}</Text>
                  <Text variant="label" className="!text-content">{parseData.distinct_clients.length}</Text>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setReviewFilter('matched')}
                  className={`h-auto min-h-0 justify-start gap-2 rounded-lg bg-surface-secondary px-3 py-2 font-normal ${
                    reviewFilter === 'matched' ? 'ring-2 ring-inset ring-emerald-400/50' : ''
                  }`}
                >
                  <Text variant="caption" className="text-content-muted">{tr('amlImportStatMatched')}</Text>
                  <Text variant="label" className="!text-emerald-400">{matchedCount}</Text>
                </Button>
                {aiMatchedCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewFilter('ai')}
                    className={`h-auto min-h-0 justify-start gap-2 rounded-lg bg-surface-secondary px-3 py-2 font-normal ${
                      reviewFilter === 'ai' ? 'ring-2 ring-inset ring-purple-400/50' : ''
                    }`}
                  >
                    <Sparkles size={12} className="text-purple-400 shrink-0" />
                    <Text variant="caption" className="text-content-muted">{tr('amlImportStatAiSuggested')}</Text>
                    <Text variant="label" className="!text-purple-400">{aiMatchedCount}</Text>
                  </Button>
                )}
                {unresolvedCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewFilter('unresolved')}
                    className={`h-auto min-h-0 justify-start gap-2 rounded-lg bg-surface-secondary px-3 py-2 font-normal ${
                      reviewFilter === 'unresolved' ? 'ring-2 ring-inset ring-amber-400/50' : ''
                    }`}
                  >
                    <Text variant="caption" className="text-content-muted">{tr('amlImportStatUnresolved')}</Text>
                    <Text variant="label" className="!text-amber-400">{unresolvedCount}</Text>
                  </Button>
                )}
                {skippedCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReviewFilter('skipped')}
                    className={`h-auto min-h-0 justify-start gap-2 rounded-lg bg-surface-secondary px-3 py-2 font-normal ${
                      reviewFilter === 'skipped' ? 'ring-2 ring-inset ring-content-muted/50' : ''
                    }`}
                  >
                    <Text variant="caption" className="text-content-muted">{tr('amlImportStatSkipped')}</Text>
                    <Text variant="label" className="!text-content-muted">{skippedCount} ({skippedRowCount} rows)</Text>
                  </Button>
                )}
              </div>

              {parseData.errors.length > 0 && (
                <Alert variant="warning" title={tr('amlImportParseWarnings')} className="!p-3">
                  <p className="mb-2">{tr('amlImportParseWarningsHint')}</p>
                  <ul className="list-disc pl-4 space-y-1 text-content-secondary">
                    {parseData.errors.map((err, i) => (
                      <li key={i} className="text-xs">{err}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <div className="rounded-xl border border-subtle">
                <div className="grid grid-cols-[15%_13%_12%_25%_25%_10%] text-2xs font-medium text-content-muted uppercase tracking-wider px-4 py-3 border-b border-subtle bg-surface-secondary/50 items-center">
                  <span>{tr('amlImportColCustomer')}</span>
                  <span>{tr('amlImportColLocation')}</span>
                  <span>{tr('amlImportColStatus')}</span>
                  <span>{tr('amlImportColAmlCustomer')}</span>
                  <span>{tr('amlImportColAmlLocation')}</span>
                  <span />
                </div>
                <div className="divide-y divide-subtle">
                  {filteredDistinctClients.length === 0 && reviewFilter !== 'all' && (
                    <div className="px-4 py-8 text-center">
                      <Text variant="body" className="text-content-secondary text-sm">
                        {tr('amlImportFilterEmpty')}
                      </Text>
                    </div>
                  )}
                  {filteredDistinctClients.map(compositeKey => {
                    const { client: clientName, location: locationName } = splitCompositeKey(compositeKey);
                    const match = parseData.matches[compositeKey];
                    const resolved = resolvedMappings[compositeKey];
                    const isMatched = match?.status === 'matched';
                    const isAiMatched = match?.status === 'ai_matched';
                    const aiSugg = aiSuggestions[compositeKey];
                    const isAiAccepted = acceptedAi.has(compositeKey);
                    const isSkipped = skippedClients.has(compositeKey);
                    const matchedCustomer = match?.customer_id
                      ? parseData.all_customers.find((c) => c.id === match.customer_id)
                      : undefined;
                    const resolvedCustomer = resolved?.customer_id
                      ? parseData.all_customers.find((c) => c.id === resolved.customer_id)
                      : undefined;
                    const screeningCustomer = resolvedCustomer ?? matchedCustomer;
                    const isUnscreened = !isSkipped && !!screeningCustomer
                      && screeningCustomer.screening_status == null;
                    const isOfacBlocked = screeningCustomer?.screening_status === 'potential_match'
                      || (isSkipped && parseData.all_customers.some(
                        (c) => c.screening_status === 'potential_match'
                          && (c.name === match?.customer_name || c.name === clientName),
                      ));

                    const hasCustomer = !!(resolved?.customer_id);
                    const hasLocation = !!(resolved?.location_id);
                    const locOptions = hasCustomer ? locationOptionsForCustomer(resolved!.customer_id) : [];
                    const needsLocationPick = hasCustomer && !hasLocation && locOptions.length > 1;

                    const confidenceBadge = (conf: string) => {
                      const map = {
                        high: { variant: 'success' as const, label: tr('amlImportBadgeConfidenceHigh') },
                        medium: { variant: 'warning' as const, label: tr('amlImportBadgeConfidenceMedium') },
                        low: { variant: 'danger' as const, label: tr('amlImportBadgeConfidenceLow') },
                      };
                      const c = map[conf as keyof typeof map] ?? map.low;
                      return <Badge variant={c.variant} size="sm">{c.label}</Badge>;
                    };

                    const actionButton = isSkipped ? (
                      isOfacBlocked ? null : (
                      <Tooltip content={tr('amlImportTooltipRestore')}>
                        <IconButton
                          icon={<RotateCcw size={13} />}
                          variant="ghost"
                          size="sm"
                          aria-label={tr('amlImportTooltipRestore')}
                          onClick={() => handleUnskip(compositeKey)}
                        />
                      </Tooltip>
                      )
                    ) : isAiMatched && aiSugg && !isAiAccepted && !resolved ? (
                      <Tooltip content={tr('amlImportTooltipRejectManual')}>
                        <IconButton
                          icon={<X size={13} />}
                          variant="ghost"
                          size="sm"
                          aria-label={tr('amlImportTooltipRejectManual')}
                          onClick={() => handleRejectAi(compositeKey)}
                        />
                      </Tooltip>
                    ) : isAiMatched && (isAiAccepted || resolved) ? (
                      <Tooltip content={tr('amlImportTooltipUndoAiMatch')}>
                        <IconButton
                          icon={<X size={13} />}
                          variant="ghost"
                          size="sm"
                          aria-label={tr('amlImportTooltipUndoAiMatch')}
                          onClick={() => handleRejectAi(compositeKey)}
                        />
                      </Tooltip>
                    ) : (!isMatched && !hasCustomer) ? (
                      <Tooltip content={tr('amlImportTooltipSkipClient')}>
                        <IconButton
                          icon={<X size={13} />}
                          variant="ghost"
                          size="sm"
                          aria-label={tr('amlImportTooltipSkipClient')}
                          onClick={() => handleSkip(compositeKey)}
                        />
                      </Tooltip>
                    ) : null;

                    const customerCell = isSkipped ? (
                      <span className="text-[11px] text-content-muted italic truncate block">{tr('amlImportRowsNotImported')}</span>
                    ) : (isMatched || isAiAccepted) && hasCustomer ? (
                      <Tooltip content={resolved!.customer_name}>
                        <span className="text-sm text-content-secondary truncate block">{resolved!.customer_name}</span>
                      </Tooltip>
                    ) : isAiMatched && aiSugg && !isAiAccepted ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Tooltip content={aiSugg.customer_name}>
                          <span className="text-sm text-purple-300 truncate block min-w-0">{aiSugg.customer_name}</span>
                        </Tooltip>
                        <Tooltip content={tr('amlImportTooltipAcceptAiSuggestion')}>
                          <IconButton
                            icon={<Check size={13} />}
                            variant="outline"
                            size="sm"
                            aria-label={tr('amlImportTooltipAcceptAiSuggestion')}
                            className="!border-purple-500/40 !text-purple-400 hover:!bg-purple-500/10 shrink-0"
                            onClick={() => handleAcceptAi(compositeKey)}
                          />
                        </Tooltip>
                      </div>
                    ) : hasCustomer ? (
                      <Tooltip content={resolved!.customer_name}>
                        <span className="text-sm text-content-secondary truncate block">{resolved!.customer_name}</span>
                      </Tooltip>
                    ) : (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="flex-1 min-w-0">
                          <Select
                            value=""
                            onChange={(val) => handleResolve(compositeKey, val)}
                            options={customerOptions}
                            placeholder={tr('amlImportSelectCustomer')}
                            size="sm"
                            searchable
                          />
                        </div>
                        <Tooltip content={tr('amlImportTooltipCreateNewCustomer')}>
                          <IconButton
                            icon={<Plus size={13} />}
                            variant="outline"
                            size="sm"
                            aria-label={tr('amlImportTooltipCreateNewCustomer')}
                            className="shrink-0"
                            onClick={() => setCreatingFor(compositeKey)}
                          />
                        </Tooltip>
                      </div>
                    );

                    const locationCell = isSkipped ? (
                      <span className="text-sm text-content-muted">—</span>
                    ) : hasLocation ? (
                        <Tooltip content={resolved!.location_name!}>
                          <span className="text-sm text-content-secondary truncate block">{resolved!.location_name}</span>
                        </Tooltip>
                      ) : needsLocationPick ? (
                        <Select
                          value=""
                          onChange={(val) => handleResolveLocation(compositeKey, val)}
                          options={locOptions}
                          placeholder={tr('amlImportSelectLocation')}
                          size="sm"
                          searchable
                        />
                      ) : hasCustomer ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-sm text-content-muted">—</span>
                          {locOptions.length === 0 && (
                            <Tooltip content={tr('amlImportBtnAddLocation')}>
                              <IconButton
                                icon={<Plus size={13} />}
                                variant="outline"
                                size="sm"
                                aria-label={tr('amlImportBtnAddLocation')}
                                className="shrink-0"
                                onClick={() => setCreatingFor(compositeKey)}
                              />
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-content-muted">—</span>
                      );

                    return (
                      <div
                        key={compositeKey}
                        className={`grid grid-cols-[15%_13%_12%_25%_25%_10%] items-center px-4 py-2.5 transition-colors hover:bg-surface-primary ${isSkipped ? 'opacity-40' : ''}`}
                      >
                        <span className={`pr-2 text-sm text-content-secondary truncate ${isSkipped ? 'line-through' : ''}`}>
                          {clientName}
                        </span>

                        <span className={`pr-2 text-xs text-content-muted truncate ${isSkipped ? 'line-through' : ''}`}>
                          {locationName || '—'}
                        </span>

                        <div className="flex items-center gap-1 flex-nowrap overflow-hidden">
                          {isSkipped ? (
                            <Badge variant={isOfacBlocked ? 'danger' : 'neutral'} size="sm">
                              {isOfacBlocked ? tr('amlImportBadgeOfacBlocked') : tr('amlImportBadgeSkipped')}
                            </Badge>
                          ) : isUnscreened ? (
                            <Badge variant="info" size="sm">{tr('amlScreeningResultNotScreened')}</Badge>
                          ) : isMatched && hasCustomer && hasLocation ? (
                            <Badge variant="success" size="sm" dot>{tr('amlImportBadgeMatched')}</Badge>
                          ) : isAiMatched && aiSugg ? (
                            <>
                              <Badge variant="info" size="sm">
                                <Sparkles size={10} className="mr-0.5 inline" />{tr('amlImportBadgeAi')}
                              </Badge>
                              {confidenceBadge(aiSugg.confidence)}
                            </>
                          ) : hasCustomer && hasLocation ? (
                            <Badge variant="success" size="sm" dot>{tr('amlImportBadgeResolved')}</Badge>
                          ) : hasCustomer ? (
                            <Badge variant="warning" size="sm" dot>{tr('amlImportColLocation')}</Badge>
                          ) : (
                            <Badge variant="warning" size="sm" dot>{tr('amlImportBadgeUnmatched')}</Badge>
                          )}
                        </div>

                        <div className="pr-2 min-w-0 relative">
                          {customerCell}
                        </div>

                        <div className="pr-2 min-w-0 relative">
                          {locationCell}
                        </div>

                        <div className="flex justify-end">
                          {actionButton}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {unscreenedMappedCount > 0 && (
                <Alert variant="info" title={tr('amlImportOfacUnscreenedTitle')} className="!p-3">
                  <Text variant="caption" className="text-content-secondary">
                    {tr('amlImportOfacUnscreenedBody', { count: unscreenedMappedCount })}
                  </Text>
                </Alert>
              )}
              {unresolvedCount > 0 && (
                <div className="flex items-center gap-2 rounded-xl border border-status-warning/20 bg-status-warning/5 p-3">
                  <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                  <Text variant="caption" className="text-content-secondary">
                    {tr('amlImportResolveAllUnmatched')}
                  </Text>
                </div>
              )}
              {ofacSkippedCount > 0 && (
                <Alert variant="warning" title={tr('amlImportOfacSkippedTitle')} className="!p-3">
                  <Text variant="caption" className="text-content-secondary">
                    {tr('amlImportOfacSkippedBody', { count: ofacSkippedCount })}
                  </Text>
                </Alert>
              )}
            </>
          )}

          {/* ── Step 3: Result ── */}
          {step === 'result' && importResult && (() => {
            const { imported, duplicates, errors, skipped } = importResult;
            const issueCount = errors.length;
            const hasIssues = issueCount > 0;
            const allDuplicatesOnly = imported > 0 && duplicates === imported && !hasIssues;

            const variant = hasIssues && imported === 0
              ? 'error'
              : hasIssues
                ? 'warning'
                : allDuplicatesOnly
                  ? 'info'
                  : 'success';

            const title = hasIssues && imported === 0
              ? tr('amlImportResultFailedTitle')
              : hasIssues
                ? tr('amlImportResultPartialTitle')
                : allDuplicatesOnly
                  ? tr('amlImportResultAllDuplicatesTitle')
                  : tr('amlImportResultSuccessTitle');

            const summary = hasIssues && imported === 0
              ? tr('amlImportResultFailedSummary', { issues: issueCount })
              : hasIssues
                ? tr('amlImportResultPartialSummary', { imported, issues: issueCount })
                : allDuplicatesOnly
                  ? tr('amlImportResultAllDuplicatesSummary', { count: duplicates })
                  : tr('amlImportResultSuccessSummary', { count: imported });

            return (
              <Alert variant={variant} title={title} className="!p-4">
                <div className="space-y-2">
                  <p className="text-sm text-content">{summary}</p>
                  {!allDuplicatesOnly && (duplicates > 0 || skipped > 0) && (
                    <p className="text-xs text-content-secondary">
                      {[
                        duplicates > 0 && tr('amlImportResultDuplicatesSkipped', { count: duplicates }),
                        skipped > 0 && tr('amlImportResultRowsSkippedUnmatched', { count: skipped }),
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {hasIssues && (
                    <div className="pt-2 mt-1 border-t border-subtle">
                      <p className="text-xs font-medium text-content mb-1">
                        {tr('amlImportResultRowProblemsHeading')}
                      </p>
                      <p className="text-2xs text-content-muted mb-2">
                        {tr('amlImportResultRowProblemsHint')}
                      </p>
                      <ul className="text-xs text-content-secondary max-h-44 overflow-y-auto space-y-1 list-disc pl-4">
                        {errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Alert>
            );
          })()}
        </Modal.Body>

        <Modal.Footer>
          {step === 'upload' && (
            <>
              <Button variant="ghost" size="lg" onClick={handleClose}>
                {tr('amlBtnCancel')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                loading={parsing}
                disabled={!file || resuming}
                leftIcon={<Upload size={14} />}
                onClick={handleParse}
              >
                {tr('amlImportBtnUploadParse')}
              </Button>
            </>
          )}

          {step === 'review' && (
            <>
              <Button variant="ghost" size="lg" onClick={handleBack}>
                {tr('amlImportBtnBack')}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                loading={savingDraft}
                leftIcon={<Save size={14} />}
                onClick={handleSaveForLater}
              >
                {tr('amlImportBtnSaveForLater')}
              </Button>
              <Button
                variant="success"
                size="lg"
                loading={committing}
                disabled={!allResolved}
                leftIcon={<Upload size={14} />}
                onClick={handleCommit}
              >
                {tr('amlImportBtnSubmitImport')}
              </Button>
            </>
          )}

          {step === 'result' && (
            <Button variant="ghost" size="lg" onClick={handleClose}>
              {tr('amlImportBtnClose')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {creatingFor && (
        <CreateCustomerModal
          clientName={resolvedMappings[creatingFor]?.customer_name || splitCompositeKey(creatingFor).client}
          fileLocationName={splitCompositeKey(creatingFor).location}
          existingCustomers={parseData.all_customers}
          onCreated={(cust) => handleCustomerCreated(creatingFor, cust)}
          onCancel={() => setCreatingFor(null)}
        />
      )}
    </>
  );
};

export default TransactionImportForm;
